import { useState, useMemo } from 'react';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isSameDay, isToday,
  isSameWeek, addMonths, subMonths, format,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const DAY_NAMES = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];

interface MiniCalendarProps {
  date: Date;
  onDateChange: (d: Date) => void;
}

export function MiniCalendar({ date, onDateChange }: MiniCalendarProps) {
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(date));

  const days = useMemo(() => {
    const start = startOfWeek(viewMonth, { weekStartsOn: 1 });
    const end   = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [viewMonth]);

  function handleDayClick(day: Date) {
    onDateChange(day);
    if (!isSameMonth(day, viewMonth)) {
      setViewMonth(startOfMonth(day));
    }
  }

  return (
    <div className="px-3 pt-3 pb-4">
      {/* Month header */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-bold text-text-primary capitalize tracking-wide">
          {format(viewMonth, 'MMMM yyyy', { locale: es })}
        </p>
        <div className="flex items-center">
          <button
            onClick={() => setViewMonth(m => subMonths(m, 1))}
            className="p-1 rounded hover:bg-gray-100 text-text-secondary hover:text-text-primary transition-colors"
          >
            <ChevronLeft size={13} />
          </button>
          <button
            onClick={() => setViewMonth(m => addMonths(m, 1))}
            className="p-1 rounded hover:bg-gray-100 text-text-secondary hover:text-text-primary transition-colors"
          >
            <ChevronRight size={13} />
          </button>
        </div>
      </div>

      {/* Day name headers */}
      <div className="grid grid-cols-7 mb-0.5">
        {DAY_NAMES.map(d => (
          <div key={d} className="text-center text-[9px] font-semibold text-text-secondary uppercase py-1 select-none">
            {d}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-y-0.5">
        {days.map(day => {
          const inMonth     = isSameMonth(day, viewMonth);
          const todayDay    = isToday(day);
          const selected    = isSameDay(day, date);
          const inCurWeek   = isSameWeek(day, date, { weekStartsOn: 1 });

          return (
            <button
              key={day.toISOString()}
              onClick={() => handleDayClick(day)}
              tabIndex={inMonth ? 0 : -1}
              className={cn(
                'h-6 w-6 mx-auto flex items-center justify-center rounded-full text-[11px] transition-colors select-none',
                selected && 'bg-primary-500 text-white font-bold',
                !selected && todayDay && 'bg-primary-100 text-primary-700 font-bold',
                !selected && !todayDay && inCurWeek && inMonth && 'bg-gray-100 text-text-primary',
                !selected && !todayDay && !inCurWeek && inMonth && 'text-text-primary hover:bg-gray-100',
                !inMonth && 'text-gray-300 hover:bg-gray-50',
              )}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>
    </div>
  );
}
